import {
  Bell,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  CreditCard,
  ReceiptText,
  Search,
  ShieldCheck,
  Sparkles,
  WalletCards,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getPublicAcademyBySlug, getStudentCourseSignups, getStudentTransactions, getTenant } from '../../firebase/firestore';
import { functions } from '../../firebase/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { getHostnameContext } from '../../utils/hostname';
import { sendFeePaymentCommunications } from '../../services/communicationService';
import { StudentSidebar } from './StudentSidebar';
import { httpsCallable } from 'firebase/functions';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function money(amount, currency) {
  return formatCurrency(Number(amount || 0), currency);
}

function dateLabel(value) {
  const date = toDate(value);
  return date ? formatDate(date) : '—';
}

function invoiceIsOverdue(record) {
  const dueDate = toDate(record.dueDate);
  const status = String(record.status || '').toLowerCase();
  return Boolean(dueDate && dueDate < new Date() && !['paid', 'settled', 'cancelled'].includes(status));
}

const PAYMENT_METHOD_LABELS = {
  paystack: 'Paystack',
  lotusPay: 'Lotus Pay',
  paybill: 'M-Pesa Paybill',
};

function normalizePaymentMethod(value) {
  return Object.prototype.hasOwnProperty.call(PAYMENT_METHOD_LABELS, value) ? value : '';
}

function verificationOutcome(method, response) {
  const payload = response || {};
  const provider = payload.result || payload.data || {};
  const status = [payload.status, payload.state, provider.status, provider.state, provider.collection_status, provider.payment_status]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');
  const resultCode = String(provider.ResultCode ?? provider.resultCode ?? '').trim();
  const providerMessage = [payload.message, payload.resultDescription, payload.resultDesc, provider.ResultDesc, provider.ResponseDescription, provider.errorMessage]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (payload.ok || ['success', 'successful', 'completed', 'complete', 'paid', 'settled'].some((value) => status.includes(value))) {
    return { state: 'success' };
  }
  if (resultCode === '1032' || /cancel|cancelled|canceled|user abort/.test(`${status} ${providerMessage}`)) {
    return { state: 'cancelled', message: 'The M-Pesa payment was cancelled. You can close this window and try again when ready.' };
  }
  if (resultCode === '1037' || /offline|timed out|timeout|not reachable|network|unavailable/.test(`${status} ${providerMessage}`)) {
    return { state: 'offline', message: 'M-Pesa could not reach the phone or the prompt timed out. Check the phone connection and try again.' };
  }
  if (['failed', 'failure', 'rejected', 'declined', 'expired'].some((value) => status.includes(value)) || /insufficient|wrong pin|declin|reject|fail|expired/.test(providerMessage) || (method === 'paybill' && resultCode === '2001')) {
    return { state: 'failed', message: providerMessage ? `M-Pesa did not complete the payment: ${providerMessage}.` : 'M-Pesa did not complete the payment. Please try again.' };
  }
  return { state: 'pending' };
}

function addBillingInterval(date, paymentTerm) {
  const next = new Date(date);
  if (paymentTerm === 'daily') next.setDate(next.getDate() + 1);
  else if (paymentTerm === 'weekly') next.setDate(next.getDate() + 7);
  else if (paymentTerm === 'monthly') next.setMonth(next.getMonth() + 1);
  else if (paymentTerm === 'per_term') next.setMonth(next.getMonth() + 3);
  else return null;
  return next;
}

function nextDueDate(admissionDate, paymentTerm, paidAmount, courseFee) {
  const start = toDate(admissionDate);
  if (!start || paymentTerm === 'full') return paymentTerm === 'full' && paidAmount < courseFee ? start : null;
  let next = new Date(start);
  const today = new Date();
  while (next < today) next = addBillingInterval(next, paymentTerm);
  return next;
}

export function StudentFinancePage({ view = 'invoices' }) {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [currency, setCurrency] = useState('KES');
  const [signedUpCourseFees, setSignedUpCourseFees] = useState(0);
  const [feeSchedules, setFeeSchedules] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState('amount');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentTenantId, setPaymentTenantId] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [paymentCourseName, setPaymentCourseName] = useState('');
  const [preferredMethod, setPreferredMethod] = useState('');
  const [paymentAcademyName, setPaymentAcademyName] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('KES');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  const paymentCommunicationKeys = useRef(new Set());

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const signups = await getStudentCourseSignups(user.uid);
        let scoped = signups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? signups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId || signup.ref?.parent?.parent?.id).filter(Boolean))];
        const groups = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenantSignups = scoped.filter((signup) => (signup.tenantId || signup.ref?.parent?.parent?.id) === tenantId);
          const tenant = await getTenant(tenantId);
          const courseIds = [...new Set(tenantSignups.map((signup) => signup.courseId).filter(Boolean))];
          const [transactions, courses] = await Promise.all([
            getStudentTransactions(
              tenantId,
              user.uid,
              user.email || tenantSignups.find((signup) => signup.email || signup.authEmail)?.email || tenantSignups.find((signup) => signup.authEmail)?.authEmail || '',
              tenantSignups.map((signup) => signup.id),
            ),
            Promise.all(courseIds.map((courseId) => getCourse(tenantId, courseId))),
          ]);
          const identifiers = new Set([user.uid, user.email, ...tenantSignups.flatMap((signup) => [signup.id, signup.studentId, signup.email, signup.authEmail, signup.authUid])]
            .filter(Boolean)
            .map((value) => String(value).toLowerCase()));
          const matched = transactions.filter((transaction) => [transaction.studentId, transaction.studentEmail, transaction.authUid]
            .filter(Boolean)
            .some((value) => identifiers.has(String(value).toLowerCase())));
          const courseFees = courses.reduce((sum, course) => {
            const fee = Number(course?.feeAmount || course?.fee || course?.price || 0);
            return sum + (Number.isFinite(fee) ? fee : 0);
          }, 0);
          const schedules = courses.map((course) => {
            const signup = tenantSignups.find((item) => item.courseId === course?.id);
            const courseFee = Number(course?.feeAmount || course?.fee || course?.price || 0);
            const paidForCourse = matched
              .filter((transaction) => transaction.courseId === course?.id)
              .reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
            return nextDueDate(
              signup?.admissionDate || signup?.admittedAt || signup?.createdAt,
              course?.paymentTerm || 'full',
              paidForCourse,
              courseFee,
            );
          }).filter(Boolean);
          return { tenantId, preferredMethod: tenant?.settings?.feePayment?.preferredMethod || '', academyName: tenant?.name || '', currency: tenant?.settings?.currency || 'KES', studentEmail: tenantSignups.find((signup) => signup.email || signup.authEmail)?.email || tenantSignups.find((signup) => signup.authEmail)?.authEmail || '', studentPhone: tenantSignups.find((signup) => signup.phone || signup.phoneNumber)?.phone || tenantSignups.find((signup) => signup.phoneNumber)?.phoneNumber || '', paymentCourseName: tenantSignups.map((signup) => signup.courseName).filter(Boolean)[0] || courses.find(Boolean)?.name || '', records: matched.map((transaction) => ({
            ...transaction,
            tenant,
            course: courses.find((course) => course?.id === transaction.courseId),
          })), courseFees, schedules };
        }));
        if (mounted) {
          const nextRecords = groups.flatMap((group) => group.records).sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
          setRecords(nextRecords);
          setSignedUpCourseFees(groups.reduce((sum, group) => sum + group.courseFees, 0));
          setFeeSchedules(groups.flatMap((group) => group.schedules));
          setAcademyName(nextRecords[0]?.tenant?.name || groups[0]?.records?.[0]?.tenant?.name || 'My learning space');
          setCurrency(nextRecords[0]?.tenant?.settings?.currency || groups[0]?.records?.[0]?.tenant?.settings?.currency || 'KES');
          // A student can belong to more than one academy. Payment must use
          // the academy whose configured default method is being displayed,
          // rather than always using the first signup returned by Firestore.
          const paymentGroup = groups.find((group) => normalizePaymentMethod(group.preferredMethod)) || groups[0];
          setPaymentTenantId(paymentGroup?.tenantId || '');
          setStudentEmail(paymentGroup?.studentEmail || user.email || '');
          setStudentPhone(paymentGroup?.studentPhone || user.phoneNumber || '');
          setPaymentCourseName(paymentGroup?.paymentCourseName || 'your course');
          setPreferredMethod(normalizePaymentMethod(paymentGroup?.preferredMethod));
          setPaymentAcademyName(paymentGroup?.academyName || nextRecords[0]?.tenant?.name || groups[0]?.academyName || 'My learning space');
          setPaymentCurrency(paymentGroup?.currency || nextRecords[0]?.tenant?.settings?.currency || groups[0]?.currency || 'KES');
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student finance', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, user?.email, user?.phoneNumber, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  function openPaymentModal() {
    setPaymentAmount('');
    setPaymentStep('amount');
    setPaymentStatus('idle');
    setPaymentStatusMessage('');
    setPaymentModalOpen(true);
  }

  function closePaymentModal() {
    if (paymentProcessing) return;
    setPaymentModalOpen(false);
  }

  function paymentMethodLabel(method) {
    return PAYMENT_METHOD_LABELS[method] || 'your academy payment method';
  }

  async function finishSuccessfulPayment({ paymentMethod, reference, receiptNumber, amount, paymentDate }) {
    const communicationKey = `${paymentMethod}:${reference}`;
    if (paymentCommunicationKeys.current.has(communicationKey)) return;
    paymentCommunicationKeys.current.add(communicationKey);

    let communicationResults = [];
    try {
      communicationResults = await sendFeePaymentCommunications({
        user,
        payment: {
          studentName: user.displayName || 'there',
          studentEmail: studentEmail || user.email || '',
          studentPhone: studentPhone || user.phoneNumber || '',
          courseName: paymentCourseName || 'your course',
          amount,
          paymentDate: paymentDate || formatDate(new Date()),
          paymentMethod,
          reference,
          receiptNumber: receiptNumber || reference,
        },
        academyName: paymentAcademyName || academyName,
        currency: paymentCurrency || currency,
      });
    } catch (error) {
      console.error('Payment was recorded but confirmation notifications could not be sent', error);
    }

    const notificationsFailed = communicationResults.some((result) => result.status === 'rejected');
    notify(notificationsFailed
      ? 'Payment received and recorded. Some confirmation notifications could not be sent.'
      : 'Payment received and recorded. Your email and SMS confirmation have been sent.');
    setPaymentStatus('success');
    setPaymentStatusMessage(notificationsFailed
      ? 'Payment received and recorded. Some confirmation notifications could not be sent.'
      : 'Payment received and recorded. Your email and SMS confirmation has been sent.');
    setPaymentProcessing(false);
    window.setTimeout(() => window.location.reload(), 1600);
  }

  function loadPaystackCheckout() {
    if (window.PaystackPop) return Promise.resolve(window.PaystackPop);
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-paystack-inline]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.PaystackPop));
        existingScript.addEventListener('error', () => reject(new Error('Paystack checkout could not be loaded.')));
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js';
      script.async = true;
      script.dataset.paystackInline = 'true';
      script.onload = () => resolve(window.PaystackPop);
      script.onerror = () => reject(new Error('Paystack checkout could not be loaded.'));
      document.body.appendChild(script);
    });
  }

  async function processFeePayment() {
    const amount = Number(paymentAmount);
    const checkoutEmail = studentEmail || user.email || '';
    if (!paymentTenantId) {
      notify('Your academy payment setup is unavailable right now.', 'error');
      return;
    }
    if (!checkoutEmail) {
      notify('We could not find the email used during your course sign-up.', 'error');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      notify('Enter a valid payment amount.', 'error');
      return;
    }
    if (amount > outstanding) {
      notify(`Enter an amount up to ${money(outstanding, currency)}.`, 'error');
      return;
    }
    if (preferredMethod === 'lotusPay') {
      const paymentPhone = String(studentPhone || user.phoneNumber || '').trim();
      if (!paymentPhone) {
        notify('Enter the M-Pesa phone number that should receive the payment prompt.', 'error');
        return;
      }
      setPaymentProcessing(true);
      setPaymentStatus('starting');
      setPaymentStatusMessage('Sending a secure M-Pesa prompt to your phone…');
      try {
        const initializePayment = httpsCallable(functions, 'initializeLotusPayPayment');
        const response = await initializePayment({
          tenantId: paymentTenantId,
          amount,
          contact_phone_number: paymentPhone,
          contact_name: user.displayName || studentEmail,
          description: `Student fee payment - ${paymentCourseName || 'Course fees'}`,
          metadata: { studentEmail: checkoutEmail, studentName: user.displayName || 'Student', courseName: paymentCourseName || 'Course fees', currency },
        });
        const collectionId = response.data?.collectionId;
        if (!collectionId) throw new Error('Lotus Pay did not return a payment collection.');
        notify('Payment prompt sent. Complete it on your phone.');
        setPaymentStatus('confirming');
        setPaymentStatusMessage('Approve the M-Pesa prompt on your phone. We are checking for confirmation automatically.');
        let attempts = 0;
        const verifyWhenPaid = async () => {
          attempts += 1;
          try {
            const verifyPayment = httpsCallable(functions, 'verifyLotusPayPayment');
            const verified = await verifyPayment({ tenantId: paymentTenantId, collectionId });
            const outcome = verificationOutcome('lotusPay', verified.data);
            if (outcome.state === 'success') {
              await finishSuccessfulPayment({
                paymentMethod: 'lotusPay',
                reference: collectionId,
                receiptNumber: verified.data?.data?.receiptNumber || collectionId,
                amount,
                paymentDate: verified.data?.data?.paid_at,
              });
              return;
            }
            if (['cancelled', 'offline', 'failed'].includes(outcome.state)) {
              setPaymentProcessing(false);
              setPaymentStatus(outcome.state);
              setPaymentStatusMessage(outcome.message);
              return;
            }
          } catch (error) {
            console.error('Unable to verify Lotus Pay fee payment', error);
          }
          if (attempts < 20) {
            window.setTimeout(verifyWhenPaid, 3000);
          } else {
            setPaymentProcessing(false);
            setPaymentStatus('offline');
            setPaymentStatusMessage('We could not confirm the prompt in time. Check your phone connection and M-Pesa messages before trying again.');
          }
        };
        window.setTimeout(verifyWhenPaid, 3000);
      } catch (error) {
        console.error('Unable to start Lotus Pay student fee payment', error);
        setPaymentProcessing(false);
        setPaymentStatus('failed');
        setPaymentStatusMessage(error.message || 'Unable to send the Lotus Pay prompt. Please check the number and try again.');
        notify(error.message || 'Unable to start the Lotus Pay payment. Please try again.', 'error');
      }
      return;
    }
    if (preferredMethod === 'paybill') {
      const paymentPhone = String(studentPhone || user.phoneNumber || '').trim();
      if (!paymentPhone) {
        notify('Enter the M-Pesa phone number that should receive the payment prompt.', 'error');
        return;
      }
      setPaymentProcessing(true);
      setPaymentStatus('starting');
      setPaymentStatusMessage('Sending a secure M-Pesa prompt to your phone…');
      try {
        const initializePayment = httpsCallable(functions, 'initializeMpesaPaybillPayment');
        const response = await initializePayment({ tenantId: paymentTenantId, amount, phoneNumber: paymentPhone, contactName: user.displayName || studentEmail, description: `Student fee payment - ${paymentCourseName || 'Course fees'}`, metadata: { studentEmail: checkoutEmail, studentName: user.displayName || 'Student', courseName: paymentCourseName || 'Course fees', currency } });
        const checkoutRequestId = response.data?.checkoutRequestId;
        if (!checkoutRequestId) throw new Error('Safaricom did not return a payment reference.');
        notify('M-Pesa payment prompt sent. Complete it on your phone.');
        setPaymentStatus('confirming');
        setPaymentStatusMessage('Approve the M-Pesa prompt on your phone. We are checking for confirmation automatically.');
        let attempts = 0;
        const verifyWhenPaid = async () => {
          attempts += 1;
          try {
            const verifyPayment = httpsCallable(functions, 'verifyMpesaPaybillPayment');
            const verified = await verifyPayment({ tenantId: paymentTenantId, checkoutRequestId });
            const outcome = verificationOutcome('paybill', verified.data);
            if (outcome.state === 'success') {
              await finishSuccessfulPayment({
                paymentMethod: 'paybill',
                reference: checkoutRequestId,
                receiptNumber: verified.data?.receiptNumber || checkoutRequestId,
                amount,
              });
              return;
            }
            if (['cancelled', 'offline', 'failed'].includes(outcome.state)) {
              setPaymentProcessing(false);
              setPaymentStatus(outcome.state);
              setPaymentStatusMessage(outcome.message);
              return;
            }
          } catch (error) { console.error('Unable to verify M-Pesa Paybill fee payment', error); }
          if (attempts < 20) {
            window.setTimeout(verifyWhenPaid, 3000);
          } else {
            setPaymentProcessing(false);
            setPaymentStatus('offline');
            setPaymentStatusMessage('We could not confirm the prompt in time. Check your phone connection and M-Pesa messages before trying again.');
          }
        };
        window.setTimeout(verifyWhenPaid, 3000);
      } catch (error) {
        console.error('Unable to start M-Pesa Paybill student fee payment', error);
        setPaymentProcessing(false);
        setPaymentStatus('failed');
        setPaymentStatusMessage(error.message || 'Unable to send the M-Pesa prompt. Please check the number and try again.');
        notify(error.message || 'Unable to start the M-Pesa payment. Please try again.', 'error');
      }
      return;
    }
    if (preferredMethod !== 'paystack') {
      notify(`${paymentMethodLabel(preferredMethod)} is configured, but student online checkout is not connected yet.`, 'error');
      return;
    }
    setPaymentProcessing(true);
    try {
      const initializePayment = httpsCallable(functions, 'initializePaystackPayment');
      const response = await initializePayment({
        tenantId: paymentTenantId,
        amount: Math.round(amount * 100),
        email: checkoutEmail,
        metadata: { purpose: 'Student fee payment', studentEmail: checkoutEmail, studentName: user.displayName || 'Student', studentPhone: studentPhone || user.phoneNumber || '', courseName: paymentCourseName || 'Course fees' },
      });
      const accessCode = response.data?.access_code;
      if (!accessCode) throw new Error('Paystack did not return a checkout session.');
      const PaystackPop = await loadPaystackCheckout();
      if (!PaystackPop) throw new Error('Paystack checkout is unavailable in this browser.');
      const popup = new PaystackPop();
      const reference = response.data?.reference;
      popup.resumeTransaction(accessCode);
      notify('Complete the payment in the Paystack checkout window.');
      setPaymentModalOpen(false);
      setPaymentProcessing(false);
      // The checkout has no reliable browser callback in the resume flow, so
      // verify server-side while Paystack's webhook remains the fallback.
      let attempts = 0;
      const verifyWhenPaid = async () => {
        attempts += 1;
        try {
          const verifyPayment = httpsCallable(functions, 'verifyPaystackPayment');
          const verified = await verifyPayment({ tenantId: paymentTenantId, reference });
          if (verified.data?.status === 'success') {
            await finishSuccessfulPayment({
              paymentMethod: 'paystack',
              reference,
              receiptNumber: verified.data?.receiptNumber || reference,
              amount,
            });
            return;
          }
        } catch (error) {
          console.error('Unable to verify student fee payment', error);
        }
        if (attempts < 20) window.setTimeout(verifyWhenPaid, 3000);
      };
      window.setTimeout(verifyWhenPaid, 3000);
    } catch (error) {
      console.error('Unable to start student fee payment', error);
      setPaymentProcessing(false);
      notify(error.message || 'Unable to start the payment. Please try again.', 'error');
    }
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const invoices = records.filter((record) => record.type === 'invoice' || record.category === 'invoice');
  const payments = records.filter((record) => record.category === 'school_fee' && record.type !== 'invoice');
  const rows = view === 'invoices' ? invoices : payments;
  const invoiceTotal = invoices.reduce((sum, record) => sum + Number(record.amount || 0), 0);
  const paymentTotal = payments.reduce((sum, record) => sum + Number(record.amount || 0), 0);
  const billableFees = signedUpCourseFees > 0 ? signedUpCourseFees : invoiceTotal;
  const outstanding = Math.max(0, billableFees - paymentTotal);
  const paidPercent = billableFees ? Math.min(100, Math.round((paymentTotal / billableFees) * 100)) : 0;
  const nextFeeDueDate = feeSchedules.length ? new Date(Math.min(...feeSchedules.map((date) => date.getTime()))) : null;
  const isInvoices = view === 'invoices';
  const title = isInvoices ? 'Invoices' : 'Fee Payments';
  const normalizedQuery = query.trim().toLowerCase();
  const filteredRows = rows.filter((record) => {
    const status = invoiceIsOverdue(record) ? 'overdue' : String(record.status || (isInvoices ? 'sent' : 'posted')).toLowerCase();
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    const matchesQuery = !normalizedQuery || [record.invoiceNumber, record.receiptNumber, record.courseName, record.course?.name, record.tenant?.name, status]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
    return matchesStatus && matchesQuery;
  });
  const firstName = String(user.displayName || user.email || 'Student').split(/[ @]/)[0];
  const currentDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());
  const isMpesaPayment = ['lotusPay', 'paybill'].includes(preferredMethod);
  const paymentIsWaiting = ['starting', 'confirming'].includes(paymentStatus);

  return (
    <main className="student-portal student-finance-page-new">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-finance-shell-new">
        <section className="student-finance-workspace-new">
          <header className="student-finance-page-header-new">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>{title}</h1>
              <p>{currentDate}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          {!isInvoices ? <>
            <div className={`student-fee-payment-default-new${preferredMethod ? '' : ' is-unavailable'}`}>
              <span className="student-fee-payment-default-icon"><WalletCards size={18} /></span>
              <div>
                <small>Academy default payment method</small>
                <strong>{preferredMethod ? paymentMethodLabel(preferredMethod) : 'Not configured yet'}</strong>
                <p>{preferredMethod ? 'This is the payment method selected by your academy for online fee payments.' : 'Your academy has not selected an online payment method for student payments.'}</p>
              </div>
              {preferredMethod ? <CheckCircle2 className="student-fee-payment-default-check" size={18} /> : null}
            </div>
            <div className="student-fee-payment-action-new"><div><strong>Ready to pay your fees?</strong><span>{preferredMethod ? `Pay securely using ${paymentMethodLabel(preferredMethod)}.` : 'Online payment is not available yet.'}</span></div><button type="button" onClick={openPaymentModal} disabled={!preferredMethod || !outstanding}><CircleDollarSign size={16} />Make fee payment</button></div>
          </> : null}

          <nav className="student-finance-tabs-new" aria-label="Student finance">
            <Link className={isInvoices ? 'is-active' : ''} to="/student/invoices"><ReceiptText size={15} />Invoices</Link>
            <Link className={!isInvoices ? 'is-active' : ''} to="/student/fee-payments"><WalletCards size={15} />Fee payments</Link>
          </nav>

          <div className="student-finance-metrics-new">
            <article><span className="is-violet"><ReceiptText size={18} /></span><div><small>Total invoiced</small><strong>{money(invoiceTotal, currency)}</strong></div></article>
            <article><span className="is-blue"><CheckCircle2 size={18} /></span><div><small>Total paid</small><strong>{money(paymentTotal, currency)}</strong></div></article>
            <article><span className="is-red"><CreditCard size={18} /></span><div><small>Current balance</small><strong>{money(outstanding, currency)}</strong></div></article>
          </div>

          <div className="student-finance-toolbar-new">
            <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${title.toLowerCase()}`} /></label>
            <div className="student-finance-filters-new" aria-label={`Filter ${title.toLowerCase()}`}>
              <button className={statusFilter === 'all' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('all')}>All</button>
              {isInvoices ? <button className={statusFilter === 'sent' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('sent')}>Sent</button> : <button className={statusFilter === 'posted' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('posted')}>Posted</button>}
              {isInvoices ? <button className={statusFilter === 'overdue' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('overdue')}>Overdue</button> : null}
            </div>
          </div>

          <div className="student-finance-section-heading-new">
            <div><h2>{isInvoices ? 'Invoice history' : 'Payment history'}</h2><p>{isInvoices ? 'Payment requests issued by your academy.' : 'Receipts for payments recorded by your academy.'}</p></div>
            <span>{filteredRows.length} record{filteredRows.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="student-courses-inline-loading"><span /><p>Loading {title.toLowerCase()}...</p></div>
          ) : filteredRows.length ? (
            <div className="student-finance-records-new">
              <div className="student-finance-record-head-new"><span>{isInvoices ? 'Invoice' : 'Receipt'}</span><span>Course</span><span>{isInvoices ? 'Due date' : 'Payment date'}</span><span>Amount</span><span>Status</span></div>
              {filteredRows.map((record) => {
                const rawStatus = record.status || (isInvoices ? 'sent' : 'posted');
                const status = invoiceIsOverdue(record) ? 'overdue' : String(rawStatus).toLowerCase();
                const reference = record.invoiceNumber || record.receiptNumber || `${isInvoices ? 'INV' : 'PAY'}-${String(record.id).slice(0, 7).toUpperCase()}`;
                return (
                  <Link className="student-finance-record-link-new" to={isInvoices ? `/student/invoices/${record.id}` : `/student/fee-payments/${record.id}`} key={`${record.tenant?.id}-${record.id}`}>
                    <div className="student-finance-reference-new"><span>{isInvoices ? <ReceiptText size={16} /> : <WalletCards size={16} />}</span><div><strong>{reference}</strong><small>{toDate(record.createdAt) ? formatDateTime(record.createdAt) : 'Recently recorded'}</small></div></div>
                    <div className="student-finance-course-new"><strong>{record.courseName || record.course?.name || 'Course fee'}</strong><small>{record.tenant?.name || academyName}</small></div>
                    <div className="student-finance-date-new"><strong>{dateLabel(isInvoices ? record.dueDate : (record.paymentDate || record.createdAt))}</strong><small>{isInvoices ? 'Payment due' : record.paymentMethod || 'Recorded payment'}</small></div>
                    <strong className="student-finance-amount-new">{money(record.amount, currency)}</strong>
                    <span className={`student-finance-pill-new student-finance-pill-new--${status}`}>{status.replace(/_/g, ' ')}</span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="student-courses-empty">
              {isInvoices ? <ReceiptText size={25} /> : <WalletCards size={25} />}
              <h2>{query || statusFilter !== 'all' ? `No matching ${title.toLowerCase()}` : `No ${title.toLowerCase()} yet`}</h2>
              <p>{query || statusFilter !== 'all' ? 'Try another search or status.' : isInvoices ? 'Invoices from your academy will appear here when they are issued.' : 'Your receipts will appear here after the academy records a payment.'}</p>
              {query || statusFilter !== 'all' ? <button type="button" onClick={() => { setQuery(''); setStatusFilter('all'); }}>Clear filters</button> : null}
            </div>
          )}
        </section>

        <aside className="student-finance-overview-new">
          <div className="student-finance-overview-heading-new"><h2>Payment Overview</h2><CircleDollarSign size={17} /></div>
          <div className="student-finance-rate-new" style={{ '--payment-rate': `${paidPercent * 3.6}deg` }}>
            <div><strong>{paidPercent}%</strong><small>Fees covered</small></div>
          </div>
          <div className="student-finance-breakdown-new">
            <div><span><i className="is-violet" />Total billable fees</span><strong>{money(billableFees, currency)}</strong></div>
            <div><span><i className="is-blue" />Payments</span><strong>{money(paymentTotal, currency)}</strong></div>
            <div><span><i className="is-red" />Outstanding</span><strong>{money(outstanding, currency)}</strong></div>
          </div>
          <div className="student-finance-due-new"><span><Clock3 size={17} /></span><div><small>Next fee due date</small><strong>{nextFeeDueDate ? formatDate(nextFeeDueDate) : 'No upcoming fee'}</strong></div></div>
          <div className="student-finance-note-new">
            <span><ShieldCheck size={16} /></span>
            <small><Sparkles size={11} />Finance records</small>
            <h3>Everything in one clear place.</h3>
            <p>Invoices and receipts shown here are records posted directly by your academy.</p>
          </div>
          <Link className="student-finance-dashboard-link-new" to="/student/portal">Back to dashboard <ChevronRight size={16} /></Link>
        </aside>
      </div>

      {paymentModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <section className="modal student-fee-payment-modal-new" role="dialog" aria-modal="true" aria-labelledby="fee-payment-title">
            <div className="modal-header">
              <div><span className="student-fee-payment-eyebrow">Secure student checkout</span><h2 id="fee-payment-title">{paymentStatus === 'success' ? 'Payment confirmed' : 'Make a fee payment'}</h2><p>{paymentIsWaiting ? 'Keep this window open while we confirm your M-Pesa payment.' : paymentStatus === 'success' ? 'Your payment has been recorded successfully.' : paymentStep === 'amount' ? 'Choose how much you would like to pay today.' : 'Review your payment before opening secure checkout.'}</p></div>
              <button className="icon-button" type="button" onClick={closePaymentModal} disabled={paymentProcessing} aria-label="Close payment modal"><X size={18} /></button>
            </div>
            <div className="student-fee-payment-method-new"><span><CreditCard size={17} /></span><div><small>Preferred payment method</small><strong>{paymentMethodLabel(preferredMethod)}</strong></div><CheckCircle2 size={17} /></div>
            {paymentStep === 'amount' ? <>
              {isMpesaPayment ? <label className="student-fee-payment-field-new student-fee-payment-phone-field-new"><span>M-Pesa phone number</span><div><input type="tel" inputMode="tel" autoComplete="tel" disabled={paymentProcessing} value={studentPhone} onChange={(event) => setStudentPhone(event.target.value)} placeholder="e.g. 0712 345 678" /></div><small>We’ll send the payment prompt to this number.</small></label> : null}
              <label className="student-fee-payment-field-new"><span>Amount to pay ({currency})</span><div><b>{currency === 'KES' ? 'Ksh' : currency}</b><input autoFocus type="number" min="1" max={outstanding} step="1" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} placeholder="0" /></div></label>
              <div className="student-fee-payment-balance-new"><span>Current outstanding balance</span><strong>{money(outstanding, currency)}</strong></div>
              <div className="student-fee-payment-footer-new"><button type="button" className="btn-ghost" onClick={closePaymentModal} disabled={paymentProcessing}>Cancel</button><button type="button" className="btn-primary" disabled={paymentProcessing} onClick={() => { if (isMpesaPayment && !studentPhone.trim()) notify('Enter the M-Pesa phone number that should receive the payment prompt.', 'error'); else if (Number(paymentAmount) > 0 && Number(paymentAmount) <= outstanding) setPaymentStep('confirm'); else notify('Enter an amount within your outstanding balance.', 'error'); }}>Continue <ChevronRight size={16} /></button></div>
            </> : <>
              <div className="student-fee-payment-confirm-new"><small>Payment amount</small><strong>{money(paymentAmount, currency)}</strong><p>Your payment will open in a secure {paymentMethodLabel(preferredMethod)} checkout. Once completed, the receipt will be recorded in your student account.</p></div>
              {isMpesaPayment && paymentStatus !== 'success' ? <div className="student-fee-payment-phone-summary-new"><span>M-Pesa prompt number</span><strong>{studentPhone || 'Add a phone number above'}</strong></div> : null}
              {paymentStatus !== 'idle' ? <div className={`student-fee-payment-status-new is-${paymentStatus}`} role={paymentStatus === 'success' || paymentStatus === 'cancelled' || paymentStatus === 'offline' || paymentStatus === 'failed' ? 'alert' : 'status'} aria-live="polite">{paymentIsWaiting ? <span className="student-fee-payment-spinner-new" /> : paymentStatus === 'success' ? <CheckCircle2 size={20} /> : <X size={20} />}<div><strong>{paymentStatus === 'starting' ? 'Sending payment prompt…' : paymentStatus === 'confirming' ? 'Confirming your payment…' : paymentStatus === 'success' ? 'Payment received and recorded' : paymentStatus === 'cancelled' ? 'Payment cancelled' : paymentStatus === 'offline' ? 'Payment could not be confirmed' : 'Payment not completed'}</strong><p>{paymentStatusMessage}</p></div></div> : null}
              <div className="student-fee-payment-footer-new">{paymentStatus === 'success' ? <button type="button" className="btn-primary" onClick={closePaymentModal}>Done</button> : paymentIsWaiting ? <button type="button" className="btn-primary" disabled>Confirming payment…</button> : ['cancelled', 'offline', 'failed'].includes(paymentStatus) ? <><button type="button" className="btn-ghost" onClick={closePaymentModal}>Close</button><button type="button" className="btn-primary" onClick={() => { setPaymentStatus('idle'); setPaymentStatusMessage(''); setPaymentStep('amount'); }}>Try again</button></> : <><button type="button" className="btn-ghost" onClick={() => setPaymentStep('amount')}>Back</button><button type="button" className="btn-primary" disabled={paymentProcessing} onClick={processFeePayment}>{paymentProcessing ? 'Opening checkout…' : 'Confirm & pay'} <ChevronRight size={16} /></button></>}</div>
            </>}
          </section>
        </div>
      ) : null}
    </main>
  );
}
